import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { AuditAction, AuditEntry } from "./types/AuditTypes";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import PenPaperIcon from "../../../../../components/icons/PenPaperIcon";

function getActionIcon(action: AuditAction) {
  const iconSx = { fontSize: 16, color: "#6B7280" };
  switch (action) {
    case "created":
      return <AddCircleOutlineOutlinedIcon sx={iconSx} />;
    case "updated":
      return <PenPaperIcon sx={iconSx} />;
    case "status_change":
      return <SwapHorizIcon sx={iconSx} />;
    case "deleted":
      return <DeleteOutlineIcon sx={iconSx} />;
    case "assigned":
      return <PersonOutlineIcon sx={iconSx} />;
    default:
      return <AccessTimeIcon sx={iconSx} />;
  }
}

function getRoleColor(): { bg: string; text: string } {
  return {
    bg: "#F1F5F9",
    text: "#475569",
  };
}

function formatTimestamp(date: Date): string {
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

interface AuditItemProps {
  entry: AuditEntry;
  isLast: boolean;
}

export default function AuditItem({ entry, isLast }: AuditItemProps) {
  const roleColor = getRoleColor();

  return (
    <Box sx={{ display: "flex", position: "relative", pb: isLast ? 0 : 0.5 }}>
      {/* Timeline column */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mr: 2,
          position: "relative",
          minWidth: 20,
        }}
      >
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: "50%",
            bgcolor: "#F3E4DA",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mt: "4px",
            zIndex: 1,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: "#724E41",
            }}
          />
        </Box>
        {!isLast && (
          <Box
            sx={{
              width: 2,
              flexGrow: 1,
              bgcolor: "action.hover",
              mt: 0.5,
            }}
          />
        )}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, pb: 2.5 }}>
        {/* Header row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 0.25,
          }}
        >
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              bgcolor: "background.paper", // light gray background
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {getActionIcon(entry.action)}
          </Box>

          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "16px",
              color: "#4B5563",
              lineHeight: 1.4,
            }}
          >
            {entry.userName}
          </Typography>

          {entry.userRole !== "System" && (
            <Chip
              label={entry.userRole}
              size="small"
              sx={{
                height: 20,
                fontSize: "12px",
                fontWeight: 500,
                bgcolor: roleColor.bg,
                color: roleColor.text,
                border: "none",
                "& .MuiChip-label": {
                  px: 1,
                },
              }}
            />
          )}
        </Box>

        {/* Description */}
        <Typography
          sx={{
            fontSize: "12px",
            color: "#4B5563",
            lineHeight: 1.5,
            ml: "28px",
          }}
        >
          {entry.description}
        </Typography>

        {/* Timestamp */}
        <Typography
          sx={{
            fontSize: "10px",
            color: "text.secondary",
            mt: 0.25,
            ml: "28px",
          }}
        >
          {formatTimestamp(entry.timestamp)}
        </Typography>
      </Box>
    </Box>
  );
}
